import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Scissors } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';

/** Mirrors the assembled edit into CuttingRoom and opens it for further editing. */
export default function SendToCuttingRoomButton({ productionId }: { productionId: string }) {
  const { t } = useTranslation('production');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const handleClick = async () => {
    setLoading(true);
    setResult(null);
    // Open the tab synchronously (inside the click) so the browser doesn't treat the
    // later navigation as a pop-up and block it. We point it at the project URL once
    // the backend hands one back.
    const tab = window.open('', '_blank');
    try {
      const resp = await apiFetch(`/api/export-cuttingroom/${productionId}`, { method: 'POST' });
      const data = await resp.json();
      if (data.status === 'success' && data.url) {
        if (tab) tab.location.href = data.url;
        else window.open(data.url, '_blank');
        const placed = data.timeline?.placed;
        setResult(
          typeof placed === 'number' && placed > 0
            ? t('final.cuttingRoomPlaced', { count: placed })
            : t('final.cuttingRoomOpened'),
        );
      } else {
        if (tab) tab.close();
        setResult(t('final.errorPrefix', { reason: data.error || t('final.noCuttingRoomUrl') }));
      }
    } catch {
      if (tab) tab.close();
      setResult(t('final.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant="secondary" onClick={handleClick} disabled={loading}>
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Scissors className="mr-2 h-4 w-4" />}
        {loading ? t('final.cuttingRoomOpening') : t('final.cuttingRoom')}
      </Button>
      {result && <span className="text-xs text-muted-foreground">{result}</span>}
    </div>
  );
}
